import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  CreditCard, Check, Crown, Users, FileText, Sparkles,
  AlertTriangle, RefreshCw, ExternalLink, X, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FEATURE_LABELS = {
  basic_extraction: 'AI Invoice Extraction',
  excel_export: 'Excel Export',
  analytics: 'Analytics Dashboard',
  ai_chat: 'AI Chat Assistant',
  recurring_payments: 'Loans & Rent Tracking',
  pdf_export: 'PDF/Word Export',
  multi_currency: 'Multi-Currency Support',
  audit_logs: 'Audit Logs',
  quickbooks_export: 'QuickBooks/Xero Export',
  priority_support: 'Priority Support',
  api_access: 'API Access'
};

function BillingSettings() {
  const [plans, setPlans] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [trialDays, setTrialDays] = useState(14);
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' or 'annual'

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [plansRes, subRes, usageRes] = await Promise.all([
        axios.get(`${API}/subscriptions/plans`, { withCredentials: true }),
        axios.get(`${API}/subscriptions/current`, { withCredentials: true }),
        axios.get(`${API}/subscriptions/usage`, { withCredentials: true })
      ]);
      setPlans(plansRes.data.plans);
      setTrialDays(plansRes.data.trial_days);
      setCurrentSubscription(subRes.data);
      setUsage(usageRes.data);
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
      toast.error('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    
    // Check for session_id in URL (returning from Stripe)
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const cancelled = params.get('cancelled');
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname + '?tab=billing');
    }
    
    if (cancelled) {
      toast.info('Payment cancelled');
      window.history.replaceState({}, '', window.location.pathname + '?tab=billing');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment status check timed out. Please refresh the page.');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);

    try {
      const response = await axios.get(`${API}/subscriptions/checkout/status/${sessionId}`, { 
        withCredentials: true 
      });

      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your subscription is now active.');
        setCheckingPayment(false);
        fetchData(); // Refresh data
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Error checking payment status');
      setCheckingPayment(false);
    }
  };

  const handleStartTrial = async () => {
    try {
      const response = await axios.post(`${API}/subscriptions/start-trial`, {}, { 
        withCredentials: true 
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start trial');
    }
  };

  const handleSubscribe = (plan) => {
    if (plan.tier === 'free') return;
    
    // Use Stripe Payment Links directly
    const stripeLink = billingCycle === 'annual' 
      ? plan.stripe_link_annual 
      : plan.stripe_link_monthly;
    
    if (stripeLink) {
      window.open(stripeLink, '_blank');
    } else {
      toast.error('Payment link not available');
    }
  };

  const handleCancelSubscription = async () => {
    const confirmMsg = isTrialActive 
      ? 'Are you sure you want to cancel your trial? You will be downgraded to Free immediately.'
      : 'Are you sure you want to cancel your subscription? You will be downgraded to Free at the end of your billing period.';
    
    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/subscriptions/cancel`, {}, { 
        withCredentials: true 
      });
      toast.success(response.data.message);
      fetchData(); // Refresh subscription data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    }
  };

  const handleDowngrade = async (targetTier) => {
    const tierNames = { free: 'Free', pro: 'Pro', team: 'Team' };
    if (!window.confirm(`Downgrade to ${tierNames[targetTier]}? Your current features will remain active until the end of your billing period.`)) {
      return;
    }

    try {
      const response = await axios.post(`${API}/subscriptions/downgrade`, {
        target_tier: targetTier
      }, { withCredentials: true });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule downgrade');
    }
  };

  const handleCancelDowngrade = async () => {
    try {
      const response = await axios.post(`${API}/subscriptions/cancel-downgrade`, {}, { 
        withCredentials: true 
      });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel downgrade');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-indigo-600 animate-spin" />
      </div>
    );
  }

  const currentTier = currentSubscription?.tier || 'free';
  const isTrialActive = currentSubscription?.is_trial;
  const hasScheduledDowngrade = currentSubscription?.downgrade_scheduled;
  const downgradeTo = currentSubscription?.downgrade_to;

  return (
    <div className="space-y-8">
      {/* Payment Processing Banner */}
      {checkingPayment && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-700 rounded-xl p-4 flex items-center gap-3"
        >
          <RefreshCw className="w-5 h-5 text-indigo-600 animate-spin" />
          <span className="text-indigo-700 dark:text-indigo-300">Verifying your payment...</span>
        </motion.div>
      )}

      {/* Current Subscription Status */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            Current Subscription
          </h3>
          {isTrialActive && (
            <span className="px-3 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full text-sm font-medium flex items-center gap-1">
              <Sparkles className="w-4 h-4" />
              Trial Active
            </span>
          )}
        </div>

        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
            currentTier === 'team' ? 'bg-amber-100 dark:bg-amber-900/40' :
            currentTier === 'pro' ? 'bg-emerald-100 dark:bg-emerald-900/40' :
            'bg-slate-100 dark:bg-slate-800'
          }`}>
            {currentTier === 'team' ? (
              <Crown className="w-7 h-7 text-amber-600 dark:text-amber-400" />
            ) : currentTier === 'pro' ? (
              <Zap className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
            ) : (
              <CreditCard className="w-7 h-7 text-slate-500" />
            )}
          </div>
          <div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white capitalize">
              {currentSubscription?.name || 'Free'} Plan
            </p>
            <p className="text-slate-500 dark:text-slate-400">
              {currentSubscription?.price > 0 ? `A$${currentSubscription.price}/month` : 'Free forever'}
            </p>
          </div>
        </div>

        {isTrialActive && currentSubscription?.trial_ends_at && (
          <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <p className="text-sm text-amber-700 dark:text-amber-300">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              Trial ends on {new Date(currentSubscription.trial_ends_at).toLocaleDateString()}
            </p>
          </div>
        )}

        {/* Scheduled Downgrade Notice */}
        {hasScheduledDowngrade && (
          <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-sm text-blue-700 dark:text-blue-300">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              Downgrade to <span className="font-semibold capitalize">{downgradeTo}</span> scheduled for end of billing period.
              <button 
                onClick={handleCancelDowngrade}
                className="ml-2 underline hover:no-underline"
              >
                Cancel downgrade
              </button>
            </p>
          </div>
        )}

        {/* Cancel Trial Option */}
        {isTrialActive && (
          <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">Not ready to commit?</p>
            <button
              onClick={handleCancelSubscription}
              data-testid="cancel-trial-btn"
              className="text-sm px-3 py-1.5 text-rose-600 hover:text-rose-700 dark:text-rose-400 border border-rose-200 dark:border-rose-800 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/20 transition-colors"
            >
              Cancel trial & return to Free
            </button>
          </div>
        )}

        {/* Downgrade/Cancel Options for Paid Subscriptions */}
        {currentTier !== 'free' && !isTrialActive && !hasScheduledDowngrade && (
          <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">Change your plan:</p>
            <div className="flex flex-wrap gap-2">
              {currentTier === 'team' && (
                <button
                  onClick={() => handleDowngrade('pro')}
                  data-testid="downgrade-to-pro-btn"
                  className="text-sm px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  Downgrade to Pro
                </button>
              )}
              <button
                onClick={() => handleDowngrade('free')}
                data-testid="downgrade-to-free-btn"
                className="text-sm px-3 py-1.5 border border-slate-300 dark:border-slate-600 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                Downgrade to Free
              </button>
              <button
                onClick={handleCancelSubscription}
                data-testid="cancel-subscription-btn"
                className="text-sm px-3 py-1.5 text-rose-600 hover:text-rose-700 dark:text-rose-400"
              >
                Cancel subscription
              </button>
            </div>
          </div>
        )}

        {/* Cancellation Notice for Scheduled Cancellation */}
        {currentTier !== 'free' && !isTrialActive && hasScheduledDowngrade && downgradeTo === 'free' && (
          <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Your subscription will be cancelled at the end of the billing period.
              </p>
              <button
                onClick={handleCancelDowngrade}
                data-testid="undo-cancellation-btn"
                className="text-sm px-3 py-1.5 text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
              >
                Undo cancellation
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Usage Stats */}
      {usage && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <FileText className="w-5 h-5 text-indigo-600" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Invoices This Month</span>
            </div>
            <div className="flex items-end gap-2">
              <span className="text-3xl font-bold text-slate-900 dark:text-white">
                {usage.invoices.used}
              </span>
              <span className="text-slate-500 dark:text-slate-400 mb-1">
                / {usage.invoices.limit === -1 ? '∞' : usage.invoices.limit}
              </span>
            </div>
            {usage.invoices.limit !== -1 && (
              <div className="mt-3 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    usage.invoices.used / usage.invoices.limit > 0.9 ? 'bg-rose-500' :
                    usage.invoices.used / usage.invoices.limit > 0.7 ? 'bg-amber-500' :
                    'bg-indigo-600'
                  }`}
                  style={{ width: `${Math.min(100, (usage.invoices.used / usage.invoices.limit) * 100)}%` }}
                />
              </div>
            )}
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <Users className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Team Members</span>
            </div>
            <div className="flex items-end gap-2">
              <span className="text-3xl font-bold text-slate-900 dark:text-white">
                {usage.team_members.used}
              </span>
              <span className="text-slate-500 dark:text-slate-400 mb-1">
                / {usage.team_members.limit === -1 ? '∞' : usage.team_members.limit}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Plans */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            {currentTier === 'free' ? 'Upgrade Your Plan' : 'Available Plans'}
          </h3>
          
          {/* Billing Cycle Toggle */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all flex items-center gap-1 ${
                billingCycle === 'annual'
                  ? 'bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400'
              }`}
            >
              Annual
              <span className="text-xs bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded">
                2 months free
              </span>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((plan) => {
            const isCurrentPlan = plan.tier === currentTier;
            const canStartTrial = plan.tier === 'pro' && currentTier === 'free' && !currentSubscription?.trial_used;
            const displayPrice = billingCycle === 'annual' ? plan.annual_price : plan.price;
            const displayGst = billingCycle === 'annual' ? plan.annual_gst : plan.gst;
            const displayTotal = billingCycle === 'annual' ? plan.annual_total : plan.total;
            
            return (
              <motion.div
                key={plan.tier}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`relative bg-white dark:bg-slate-900 border-2 rounded-xl p-6 ${
                  isCurrentPlan 
                    ? 'border-indigo-600 dark:border-indigo-500' 
                    : plan.tier === 'pro' 
                    ? 'border-indigo-200 dark:border-indigo-800'
                    : 'border-slate-200 dark:border-slate-700'
                }`}
              >
                {plan.tier === 'pro' && !isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-indigo-600 text-white text-xs font-medium rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                {isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-emerald-600 text-white text-xs font-medium rounded-full">
                      Current Plan
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h4 className="text-xl font-bold text-slate-900 dark:text-white capitalize">
                    {plan.name}
                  </h4>
                  <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    {plan.description}
                  </p>
                  <div className="mt-4">
                    <span className="text-4xl font-bold text-slate-900 dark:text-white">
                      A${displayPrice}
                    </span>
                    {displayPrice > 0 && (
                      <>
                        <span className="text-slate-500 dark:text-slate-400">/{billingCycle === 'annual' ? 'year' : 'month'}</span>
                        <p className="text-xs text-slate-400 mt-1">+ A${displayGst} GST = A${displayTotal} total</p>
                        {billingCycle === 'annual' && (
                          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1 font-medium">
                            Save A${((plan.total * 12) - plan.annual_total).toFixed(2)}/year
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-indigo-600" />
                    <span className="text-slate-700 dark:text-slate-300">
                      {plan.invoice_limit === -1 ? 'Unlimited' : plan.invoice_limit} invoices/month
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-indigo-600" />
                    <span className="text-slate-700 dark:text-slate-300">
                      {plan.team_members === -1 ? 'Unlimited' : plan.team_members} team member{plan.team_members !== 1 ? 's' : ''}
                    </span>
                  </div>
                  
                  <div className="border-t border-slate-100 dark:border-slate-800 pt-3 mt-3">
                    {plan.features.slice(0, 5).map((feature) => (
                      <div key={feature} className="flex items-center gap-2 text-sm py-1">
                        <Check className="w-4 h-4 text-emerald-500" />
                        <span className="text-slate-600 dark:text-slate-400">
                          {FEATURE_LABELS[feature] || feature}
                        </span>
                      </div>
                    ))}
                    {plan.features.length > 5 && (
                      <p className="text-xs text-slate-400 mt-2">
                        +{plan.features.length - 5} more features
                      </p>
                    )}
                  </div>
                </div>

                {isCurrentPlan ? (
                  <button
                    disabled
                    className="w-full py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-500 rounded-lg font-medium"
                  >
                    Current Plan
                  </button>
                ) : plan.tier === 'free' ? (
                  <button
                    disabled
                    className="w-full py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-500 rounded-lg font-medium"
                  >
                    Free Forever
                  </button>
                ) : canStartTrial ? (
                  <div className="space-y-2">
                    <button
                      onClick={handleStartTrial}
                      className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition-colors"
                    >
                      Start {trialDays}-Day Free Trial
                    </button>
                    <button
                      onClick={() => handleSubscribe(plan)}
                      className="w-full py-2 text-indigo-600 hover:text-indigo-700 text-sm font-medium"
                    >
                      Or subscribe now
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => handleSubscribe(plan)}
                    className={`w-full py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                      plan.tier === 'pro' 
                        ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                        : 'bg-purple-600 hover:bg-purple-700 text-white'
                    }`}
                  >
                    {currentTier !== 'free' && plan.price > (currentSubscription?.price || 0) ? 'Upgrade' : 'Subscribe'}
                    <ExternalLink className="w-4 h-4" />
                  </button>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* FAQ */}
      <div className="bg-slate-50 dark:bg-slate-800/50 rounded-xl p-6">
        <h4 className="font-semibold text-slate-900 dark:text-white mb-4">Frequently Asked Questions</h4>
        <div className="space-y-4 text-sm">
          <div>
            <p className="font-medium text-slate-700 dark:text-slate-300">Can I cancel anytime?</p>
            <p className="text-slate-500 dark:text-slate-400">Yes, you can cancel your subscription at any time. You&apos;ll retain access until the end of your billing period.</p>
          </div>
          <div>
            <p className="font-medium text-slate-700 dark:text-slate-300">What happens when I hit my invoice limit?</p>
            <p className="text-slate-500 dark:text-slate-400">You&apos;ll be prompted to upgrade your plan. Your existing invoices remain accessible.</p>
          </div>
          <div>
            <p className="font-medium text-slate-700 dark:text-slate-300">Can I change plans?</p>
            <p className="text-slate-500 dark:text-slate-400">Yes, you can upgrade or downgrade at any time. Changes take effect immediately.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BillingSettings;
